import { Component, effect, inject, OnDestroy } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent, FormTextareaComponent, FormDatePickerComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { NewsItemFacade } from '@app/store/news-item';

@Component({
    selector: 'app-news-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, FormTextareaComponent, FormDatePickerComponent, UnavailableComponent],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly newsItemFacade = inject(NewsItemFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    readonly newsItem = this.newsItemFacade.newsItem;
    readonly loading = this.newsItemFacade.loading;
    readonly saving = this.newsItemFacade.saving;
    readonly error = this.newsItemFacade.error;

    readonly form = this.formBuilder.group({
        title: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        description: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        date: this.formBuilder.control<Date | null>(null, [FormValidators.Required]),
    });

    constructor() {
        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.newsItemFacade.getNewsItem(id);
        }

        effect(() => {
            const newsItem = this.newsItem();
            if (newsItem) {
                this.form.patchValue({
                    title: newsItem.title,
                    description: newsItem.description,
                    date: newsItem.date,
                });
            }
        });

        effect(() => {
            if (this.newsItemFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.newsItemFacade.saving() === false && this.newsItemFacade.saved() === false) {
                this.form.enable();
            }
        });
    }

    ngOnDestroy(): void {
        this.newsItemFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const newsItem = this.newsItem();
        if (this.form.valid && newsItem) {
            this.form.disable();
            this.newsItemFacade.updateNewsItem(newsItem.id, this.form.getRawValue());
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
